import { useMemo } from "react";
import type { DashboardEmptyStateCode, TenantVerticalLiteral } from "@eduviaos/shared";
import { useDashboardSummary } from "./useDashboardSummary";

/**
 * Phase D0c — derive module-availability state from the dashboard
 * summary in a shape callers (AppShell nav, page guards,
 * ComingSoonPage) actually need.
 *
 * Returns:
 *   - enabledKeys      — Set of module keys the tenant has access to
 *   - vertical         — Tenant vertical (`school | coaching_institute |
 *                        study_centre`) or `undefined` while loading.
 *                        Phase F-finale-3: added so the designation
 *                        editor + suggest drawer can gate permission
 *                        groups by vertical without a second hook.
 *   - emptyStateOf     — `(key) => "not_enabled" | "not_implemented" |
 *                        "no_data_yet" | undefined`
 *   - isLoading        — true while the underlying summary is fetching
 *   - isError          — true if the summary failed
 *
 * The hook is intentionally thin — it wraps `useDashboardSummary` and
 * memoises a Set lookup. Components that need the full dashboard
 * payload (KPIs, recency rails) should call `useDashboardSummary`
 * directly; the two share the same TanStack cache slot.
 */
export function useEnabledModules() {
  const summary = useDashboardSummary();

  return useMemo(() => {
    const enabledKeys = new Set<string>(summary.data?.enabled_modules ?? []);
    const states = summary.data?.empty_states ?? {};
    // `tenant` can be absent when the shared cache slot holds a partial payload.
    const vertical: TenantVerticalLiteral | undefined = summary.data?.tenant?.vertical;

    return {
      enabledKeys,
      vertical,
      isLoading: summary.isPending,
      isError: summary.isError,
      isModuleEnabled: (key: string) => enabledKeys.has(key),
      emptyStateOf: (key: string): DashboardEmptyStateCode | undefined => states[key],
    };
  }, [
    summary.data?.enabled_modules,
    summary.data?.empty_states,
    summary.data?.tenant?.vertical,
    summary.isPending,
    summary.isError,
  ]);
}
